// ChatManager.js - SQL.js Implementation with Real-time Updates
import initSqlJs from "sql.js";
import { v4 as uuidv4 } from "uuid";
import { generateUUID } from "./util";
import { getLogger } from "./UnifiedLogger";
import { openDB } from "idb";
const MAX_CHAT_STORE = 6;

const { logInfo, logError } = getLogger("ChatManager", false);

let SQL;
let db;

let idbInstance;

async function getIDB() {
  if (!idbInstance) {
    idbInstance = await openDB("chat_app", 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains("sqlite")) {
          db.createObjectStore("sqlite");
        }
      },
    });
  }
  return idbInstance;
}

const initDB = async () => {
  if (!SQL) {
    SQL = await initSqlJs({
      locateFile: (file) => {
        try {
          if (
            typeof window !== "undefined" &&
            window.process &&
            window.process.versions &&
            window.process.versions.electron
          ) {
            const path = window.require("path");
            const localPath = path.join(__dirname, "sqljs", file);
            return localPath;
          }
        } catch (e) {
          logError(
            "INIT_DB_ERROR",
            "Local file system access not available, using CDN",
            { error: e.message }
          );
        }
        return `https://sql.js.org/dist/${file}`;
      },
    });

    const database = await getIDB();
    const storedData = await database.get("sqlite", "chat_db");

    if (storedData) {
      try {
        db = new SQL.Database(storedData);
        logInfo("DB_LOAD", "Database loaded from IndexedDB");
      } catch (error) {
        console.warn(
          "Failed to load existing database, creating new one:",
          error
        );
        db = new SQL.Database();
      }
    } else {
      db = new SQL.Database();
    }

    db.run(`
      CREATE TABLE IF NOT EXISTS chats (
        chatId TEXT,
        messageId TEXT PRIMARY KEY,
        data TEXT,
        timestamp INTEGER,
        UNIQUE(chatId, messageId)
      );
    `);

    db.run(`
      CREATE TABLE IF NOT EXISTS recent_chats (
        chatId TEXT PRIMARY KEY,
        lastActivity INTEGER
      );
    `);

    db.run(`CREATE INDEX IF NOT EXISTS idx_chats_chatId ON chats(chatId);`);
    db.run(
      `CREATE INDEX IF NOT EXISTS idx_chats_timestamp ON chats(timestamp);`
    );
    db.run(
      `CREATE INDEX IF NOT EXISTS idx_recent_lastActivity ON recent_chats(lastActivity DESC);`
    );
  }
};

export const MESSAGE_DIRECTIONS = {
  OUTGOING: "outgoing",
  INCOMING: "incoming",
};

export const MESSAGE_STATUSES = {
  SENT: "sent",
  RECEIVED: "received",
  READ: "read",
  ERROR: "error",
  PENDING: "pending",
};

export const MESSAGE_TYPES = {
  TEXT: "text",
  IMAGE: "image",
  AUDIO: "audio",
  VIDEO: "video",
  FILE: "file",
};

export const generateChatId = () => generateUUID();
export const generatedBotMessageId = () => `bot_message_${generateUUID()}`;
export const generatedUserMessageId = () => `user_message_${generateUUID()}`;

export const generateTimestamp = () => {
  const now = new Date();
  const date = now.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const time = now.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });

  return `${date} • ${time}`;
};

export class ChatManager {
  constructor() {
    this.chatList = [];
    this.recentChatOrder = [];
    this.ready = false;
    this.eventListeners = {
      "message-added": [],
      "message-updated": [],
      "chat-updated": [],
      "recent-chats-changed": [],
      "database-refreshed": [],
      "chat-deleted": [],
    };
    this.initPromise = this._initialize();
  }

  on(eventName, callback) {
    if (this.eventListeners[eventName]) {
      this.eventListeners[eventName].push(callback);
    }
  }

  off(eventName, callback) {
    if (this.eventListeners[eventName]) {
      this.eventListeners[eventName] = this.eventListeners[eventName].filter(
        (cb) => cb !== callback
      );
    }
  }

  _emit(eventName, data) {
    if (this.eventListeners[eventName]) {
      this.eventListeners[eventName].forEach((callback) => {
        try {
          callback(data);
          logInfo("EVENT_EMITTED", `Event emitted: ${eventName}`, {
            eventName,
            data,
          });
        } catch (e) {
          console.error(`Error in ${eventName} listener:`, e);
        }
      });
    }
  }

  async _initialize() {
    try {
      await initDB();
      await this._loadFromDB();
      await this._cleanupExcessChatsOnInit();

      this.ready = true;
      logInfo("CHAT_MANAGER_INIT", "ChatManager initialized successfully");
      this._emit("database-refreshed");
    } catch (error) {
      logError("CHAT_MANAGER_INIT_ERROR", "Failed to initialize ChatManager", {
        error: error.message,
      });
      this.chatList = [];
      this.recentChatOrder = [];
      this.ready = true;
      this._emit("database-refreshed");
    }
  }

  async _ensureReady() {
    if (!this.ready) {
      await this.initPromise;
    }
  }

  uint8ToString(uint8Array) {
    let result = "";
    const chunkSize = 0x8000;
    for (let i = 0; i < uint8Array.length; i += chunkSize) {
      result += String.fromCharCode.apply(
        null,
        uint8Array.subarray(i, i + chunkSize)
      );
    }
    return result;
  }

  async _saveDBToLocal() {
    try {
      if (!db) return;
      const data = db.export(); // Uint8Array
      const database = await getIDB();
      await database.put("sqlite", data, "chat_db");
      logInfo("DB_SAVE", "Database saved to IndexedDB");
    } catch (error) {
      logError("DB_SAVE_ERROR", "Failed to save database", {
        error: error.message,
      });
    }
  }

  /**
   * ✅ ADD THIS ENTIRE METHOD
   * Enforce MAX_CHAT_STORE limit by removing oldest chats
   */
  async _enforceMaxChatLimit() {
    try {
      const totalChats = this.chatList.length;

      if (totalChats > MAX_CHAT_STORE) {
        const chatsToRemove = totalChats - MAX_CHAT_STORE;

        // Get oldest chats (at the end of recentChatOrder array)
        const oldestChatIds = this.recentChatOrder.slice(-chatsToRemove);

        logInfo(
          "ENFORCE_CHAT_LIMIT",
          `Removing ${chatsToRemove} oldest chat(s)`,
          {
            totalChats,
            maxLimit: MAX_CHAT_STORE,
            chatsToRemove: oldestChatIds,
          }
        );

        // Remove each old chat
        for (const chatId of oldestChatIds) {
          await this._removeChatById(chatId);
        }

        this._emit("database-refreshed");
      }
    } catch (error) {
      logError("ENFORCE_LIMIT_ERROR", "Error enforcing chat limit", {
        error: error.message,
      });
    }
  }

  /**
   * ✅ ADD THIS ENTIRE METHOD
   * Internal method to remove a chat
   */
  async _removeChatById(chatId) {
    try {
      // Remove from memory
      this.chatList = this.chatList.filter((chat) => chat.chatId !== chatId);
      this.recentChatOrder = this.recentChatOrder.filter((id) => id !== chatId);

      // Remove from database
      db.run(`DELETE FROM chats WHERE chatId = ?`, [chatId]);
      db.run(`DELETE FROM recent_chats WHERE chatId = ?`, [chatId]);

      logInfo("CHAT_REMOVED", `Chat removed: ${chatId}`);

      // Emit event for this specific chat deletion
      this._emit("chat-deleted", { chatId });
    } catch (error) {
      logError("REMOVE_CHAT_ERROR", "Error removing chat", {
        chatId,
        error: error.message,
      });
    }
  }

  /**
   * ✅ ADD THIS NEW METHOD
   * Cleanup existing chats if they exceed MAX_CHAT_STORE
   * Called during initialization to handle browsers with too many stored chats
   */
  async _cleanupExcessChatsOnInit() {
    try {
      const totalChats = this.chatList.length;

      if (totalChats > MAX_CHAT_STORE) {
        const chatsToRemove = totalChats - MAX_CHAT_STORE;
        const oldestChatIds = this.recentChatOrder.slice(-chatsToRemove);

        logInfo(
          "INIT_CLEANUP",
          `Removing ${chatsToRemove} excess chat(s) from browser storage`,
          {
            totalChats,
            maxLimit: MAX_CHAT_STORE,
            chatsToRemove: oldestChatIds,
          }
        );

        for (const chatId of oldestChatIds) {
          // ✅ Log BEFORE removal
          // console.log(`🗑️ [CLEANUP] Removing chat: ${chatId.substring(0, 8)}`);

          this.chatList = this.chatList.filter(
            (chat) => chat.chatId !== chatId
          );
          this.recentChatOrder = this.recentChatOrder.filter(
            (id) => id !== chatId
          );

          db.run(`DELETE FROM chats WHERE chatId = ?`, [chatId]);
          db.run(`DELETE FROM recent_chats WHERE chatId = ?`, [chatId]);

          logInfo(
            "INIT_CHAT_REMOVED",
            `Excess chat removed during init: ${chatId}`
          );
        }

        await this._saveDBToLocal();

        // ✅ Log AFTER cleanup
        // console.log("✅ [CLEANUP] Cleanup complete!", {
        //   previousCount: totalChats,
        //   currentCount: this.chatList.length,
        //   expectedCount: MAX_CHAT_STORE,
        // });

        logInfo(
          "INIT_CLEANUP_COMPLETE",
          `Cleanup complete. Chats reduced from ${totalChats} to ${this.chatList.length}`
        );

        this._emit("database-refreshed");
        this._emit("chats-cleaned-up", {
          removedCount: chatsToRemove,
          remainingCount: this.chatList.length,
        });

        return true;
      }

      logInfo("INIT_CLEANUP_SKIP", "No excess chats to clean up", {
        totalChats,
        maxLimit: MAX_CHAT_STORE,
      });

      return false;
    } catch (error) {
      console.error("❌ [CLEANUP] Error during cleanup:", error);
      logError("INIT_CLEANUP_ERROR", "Error during initialization cleanup", {
        error: error.message,
      });
      return false;
    }
  }

  async _cleanupOldData() {
    try {
      const oldChatsResult = db.exec(`
        SELECT chatId FROM recent_chats
        ORDER BY lastActivity ASC
        LIMIT (
          SELECT COUNT(*) - 50 FROM recent_chats
        )
      `);

      if (oldChatsResult.length > 0) {
        const oldChatIds = oldChatsResult[0].values.map((row) => row[0]);

        for (const chatId of oldChatIds) {
          db.run(`DELETE FROM chats WHERE chatId = ?`, [chatId]);
          db.run(`DELETE FROM recent_chats WHERE chatId = ?`, [chatId]);
          this.chatList = this.chatList.filter(
            (chat) => chat.chatId !== chatId
          );
          this.recentChatOrder = this.recentChatOrder.filter(
            (id) => id !== chatId
          );
        }

        logInfo("CHAT_CLEANUP", `Pruned old chats`, {
          cleanedChatIds: oldChatIds,
        });
        this._emit("database-refreshed");
      }
    } catch (error) {
      logError("CHAT_CLEANUP_ERROR", "Error during cleanup", {
        error: error.message,
      });
    }
  }

  async _loadFromDB() {
    try {
      const messagesResult = db.exec(`
        SELECT chatId, messageId, data, timestamp 
        FROM chats 
        ORDER BY timestamp ASC
      `);

      if (messagesResult.length > 0) {
        const groupedChats = {};

        messagesResult[0].values.forEach(([chatId, messageId, data]) => {
          try {
            const message = JSON.parse(data);
            if (!groupedChats[chatId]) {
              groupedChats[chatId] = {
                chatId,
                chatMessages: [],
              };
            }
            groupedChats[chatId].chatMessages.push(message);
          } catch (parseError) {
            console.warn(`Failed to parse message ${messageId}:`, parseError);
          }
        });

        this.chatList = Object.values(groupedChats);
      } else {
        this.chatList = [];
      }

      const recentResult = db.exec(`
        SELECT chatId FROM recent_chats 
        ORDER BY lastActivity DESC
      `);

      if (recentResult.length > 0) {
        this.recentChatOrder = recentResult[0].values.map((row) => row[0]);
      } else {
        this.recentChatOrder = [];
      }

      logInfo(
        "DB_LOAD",
        `Loaded ${this.chatList.length} chats with ${this.recentChatOrder.length} recent chats`
      );
    } catch (error) {
      console.error("Error loading from DB:", error);
      this.chatList = [];
      this.recentChatOrder = [];
    }
  }

  _getChatIndex(chatId) {
    return this.chatList.findIndex((chat) => chat.chatId === chatId);
  }

  async _updateRecentChats(chatId) {
    try {
      this.recentChatOrder = [
        chatId,
        ...this.recentChatOrder.filter((id) => id !== chatId),
      ];
      db.run(
        `
        INSERT OR REPLACE INTO recent_chats (chatId, lastActivity) 
        VALUES (?, ?)
      `,
        [chatId, Date.now()]
      );
      this._emit("recent-chats-changed", this.recentChatOrder);
    } catch (error) {
      console.error("Error updating recent chats:", error);
    }
  }

  createMessage({
    content = "",
    userData = { name: "Anonymous" },
    chatId,
    overrides = {},
  }) {
    const baseMessage = {
      id: uuidv4(),
      chatId,
      type: MESSAGE_TYPES.TEXT,
      senderId: uuidv4(),
      senderName: userData.name,
      receiverId: uuidv4(),
      timestamp: Date.now(),
      content,
      query: content,
      contentLanguage: null,
      status: MESSAGE_STATUSES.SENT,
      direction: MESSAGE_DIRECTIONS.OUTGOING,
      contentAudio: "",
      isLanguageDetecting: true,
      languageDetectedMessage: "Detecting language...",
      isBotResponding: false,
      isFileUploading: false,
      isAudioRecording: false,
      isAudioLoading: false,
      isTranslated: false,
    };

    return { ...baseMessage, ...overrides };
  }

  async addMessage(message) {
    await this._ensureReady();
    if (!message?.chatId || !message?.id) return;

    try {
      const chatIndex = this._getChatIndex(message.chatId);
      const isNewChat = chatIndex === -1;

      if (isNewChat) {
        // ✅ CHANGE: was if (chatIndex === -1)
        // ✅ ADD THESE LOG LINES
        this.chatList.push({
          chatId: message.chatId,
          chatMessages: [message],
        });

        logInfo("NEW_CHAT_CREATED", `New chat created: ${message.chatId}`, {
          totalChats: this.chatList.length,
          maxLimit: MAX_CHAT_STORE,
        });
      } else {
        const chat = this.chatList[chatIndex];
        const exists = chat.chatMessages.some((msg) => msg.id === message.id);
        if (!exists) {
          chat.chatMessages.push(message);
        }
      }

      // Write to memory + DB
      db.run(
        `INSERT OR REPLACE INTO chats (chatId, messageId, data, timestamp) 
         VALUES (?, ?, ?, ?)`,
        [message.chatId, message.id, JSON.stringify(message), message.timestamp]
      );

      // 🔹 Emit immediately so React sidebar/chat updates instantly
      this._emit("message-added", { chatId: message.chatId, message });
      this._emit("chat-updated", { chatId: message.chatId });

      // Then do slower ops
      await this._updateRecentChats(message.chatId);

      if (isNewChat) {
        await this._enforceMaxChatLimit();
      }

      await this._saveDBToLocal();
    } catch (error) {
      logError("ADD_MESSAGE_ERROR", "Error adding message", {
        error: error.message,
        chatId: message.chatId,
        messageId: message.id,
      });
    }
  }

  async updateMessage(chatId, messageId, updatedData) {
    await this._ensureReady();

    try {
      const chatIndex = this._getChatIndex(chatId);
      if (chatIndex === -1) return;

      const messages = this.chatList[chatIndex].chatMessages;
      const index = messages.findIndex((msg) => msg.id === messageId);
      if (index !== -1) {
        messages[index] = { ...messages[index], ...updatedData };

        // Update in database
        db.run(
          `
          UPDATE chats 
          SET data = ?, timestamp = ? 
          WHERE chatId = ? AND messageId = ?
        `,
          [
            JSON.stringify(messages[index]),
            messages[index].timestamp,
            chatId,
            messageId,
          ]
        );

        await this._updateRecentChats(chatId);
        await this._saveDBToLocal();

        // Emit events
        this._emit("message-updated", {
          chatId,
          messageId,
          updatedMessage: messages[index],
        });
        this._emit("chat-updated", { chatId });
      }
    } catch (error) {
      console.error("Error updating message:", error);
    }
  }

  // ChatManager.js:654 Error updating message: TypeError: Cannot read properties of undefined (reading "run')
  //     at ChatManager.updateMessage (ChatManager.js:628:12)
  //     at async chatManagerMiddleware.js:35:7

  // Delete message
  async deleteMessage(chatId, messageId) {
    await this._ensureReady();

    try {
      const chatIndex = this._getChatIndex(chatId);
      if (chatIndex === -1) return;

      const chat = this.chatList[chatIndex];
      chat.chatMessages = chat.chatMessages.filter(
        (msg) => msg.id !== messageId
      );

      // Delete from database
      db.run(`DELETE FROM chats WHERE chatId = ? AND messageId = ?`, [
        chatId,
        messageId,
      ]);

      await this._saveDBToLocal();

      // Emit events
      this._emit("message-updated", { chatId, messageId, deleted: true });
      this._emit("chat-updated", { chatId });
    } catch (error) {
      console.error("Error deleting message:", error);
    }
  }

  // Delete entire chat
  async deleteChat(chatId) {
    await this._ensureReady();

    try {
      // Remove from memory
      // this.chatList = this.chatList.filter((chat) => chat.chatId !== chatId);
      // this.recentChatOrder = this.recentChatOrder.filter((id) => id !== chatId);

      // Remove from database
      // db.run(`DELETE FROM chats WHERE chatId = ?`, [chatId]);
      // db.run(`DELETE FROM recent_chats WHERE chatId = ?`, [chatId]);
      await this._removeChatById(chatId);

      await this._saveDBToLocal();

      // Emit events
      this._emit("chat-updated", { chatId, deleted: true });
      this._emit("recent-chats-changed", this.recentChatOrder);
    } catch (error) {
      console.error("Error deleting chat:", error);
    }
  }

  // Get chat by ID
  getChatById(chatId) {
    const chat = this.chatList.find((chat) => chat.chatId === chatId);
    return chat ? chat.chatMessages : [];
  }

  // Get chat by index
  getChatByIndex = (index) => this.chatList[index]?.chatMessages || null;

  // Get recent chats
  getRecentChats = () => this.recentChatOrder;

  // Get all chats
  getAllChats = () => this.chatList;

  // Clear all chats
  async clearAllChats(keepChatId = null) {
    await this._ensureReady();

    try {
      if (keepChatId) {
        // Delete all except the specified chat
        db.run(`DELETE FROM chats WHERE id != ?`, [keepChatId]);
        db.run(`DELETE FROM recent_chats WHERE chat_id != ?`, [keepChatId]);

        // Keep only the specified chat in memory
        this.chatList = this.chatList.filter((chat) => chat.id === keepChatId);
        this.recentChatOrder = this.recentChatOrder.filter(
          (id) => id === keepChatId
        );
      } else {
        // Delete all chats
        db.run(`DELETE FROM chats`);
        db.run(`DELETE FROM recent_chats`);
        this.chatList = [];
        this.recentChatOrder = [];
      }

      await this._saveDBToLocal();

      // Emit events
      this._emit("database-refreshed");
      this._emit("recent-chats-changed", []);
    } catch (error) {
      console.error("Error clearing all chats:", error);
    }
  }

  // Refresh from database
  async refreshFromDatabase() {
    await this._ensureReady();
    await this._loadFromDB();
    this._emit("database-refreshed");
  }

  // Get database statistics
  async getStats() {
    await this._ensureReady();

    try {
      const chatCountResult = db.exec(
        `SELECT COUNT(DISTINCT chatId) as count FROM chats`
      );
      const messageCountResult = db.exec(`SELECT COUNT(*) as count FROM chats`);

      const chatCount =
        chatCountResult.length > 0 ? chatCountResult[0].values[0][0] : 0;
      const messageCount =
        messageCountResult.length > 0 ? messageCountResult[0].values[0][0] : 0;

      return {
        totalChats: chatCount,
        totalMessages: messageCount,
        memoryChats: this.chatList.length,
        recentChatsCount: this.recentChatOrder.length,
        maxChatLimit: MAX_CHAT_STORE,
      };
    } catch (error) {
      console.error("Error getting stats:", error);
      return {
        totalChats: 0,
        totalMessages: 0,
        memoryChats: this.chatList.length,
        recentChatsCount: this.recentChatOrder.length,
        maxChatLimit: MAX_CHAT_STORE,
      };
    }
  }

  // Export chats as JSON
  async exportChats() {
    await this._ensureReady();

    return {
      chats: this.chatList,
      recentOrder: this.recentChatOrder,
      exportDate: new Date().toISOString(),
      version: "1.0",
    };
  }

  // Import chats from JSON
  async importChats(importData) {
    await this._ensureReady();

    try {
      if (!importData.chats || !Array.isArray(importData.chats)) {
        throw new Error("Invalid import data format");
      }

      // Clear existing data
      await this.clearAllChats();

      // Import chats
      for (const chat of importData.chats) {
        for (const message of chat.chatMessages) {
          await this.addMessage(message);
        }
      }

      logInfo(
        "IMPORT_CHATS",
        `Imported ${importData.chats.length} chats successfully`
      );
      this._emit("database-refreshed");
      return true;
    } catch (error) {
      console.error("Error importing chats:", error);
      throw error;
    }
  }

  // Save database to file (for Electron apps)
  async saveToFile(filePath) {
    if (
      typeof window !== "undefined" &&
      window.process &&
      window.process.versions &&
      window.process.versions.electron
    ) {
      try {
        const fs = window.require("fs");
        const data = db.export();
        fs.writeFileSync(filePath, Buffer.from(data));
        return true;
      } catch (error) {
        console.error("Error saving database to file:", error);
        throw error;
      }
    } else {
      throw new Error(
        "File system access only available in Electron environment"
      );
    }
  }

  // Load database from file (for Electron apps)
  async loadFromFile(filePath) {
    if (
      typeof window !== "undefined" &&
      window.process &&
      window.process.versions &&
      window.process.versions.electron
    ) {
      try {
        const fs = window.require("fs");
        const data = fs.readFileSync(filePath);
        db = new SQL.Database(new Uint8Array(data));
        await this._loadFromDB();
        this._emit("database-refreshed");
        return true;
      } catch (error) {
        console.error("Error loading database from file:", error);
        throw error;
      }
    } else {
      throw new Error(
        "File system access only available in Electron environment"
      );
    }
  }
}
